import RequiredFormatTooltip from '@/components/file-upload/required-format-tooltip';
import { useUppyDropTarget } from '@/components/file-upload/use-file-upload';
import type { CalcSettingsUploadResult } from '@/pages/price-setup/calculation-settings/tabs/calc-settings-upload-modal';
import { themeColors } from '@/theme/colors';
import { Icon } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import { useCallback, useRef, useState } from 'react';
import CalcSettingsUploadModal from './calc-settings-upload-modal';
import DownloadButton from './download-button';

type FileUploadCardProps = {
  target: string;
  content: {
    title: string;
    description: string;
  };
};

const FileUploadCard = ({ target, content }: FileUploadCardProps) => {
  const [uploadComplete, setUploadComplete] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialFile, setInitialFile] = useState<File | undefined>(undefined);
  const [isDragging, setIsDragging] = useState(false);

  const cardRef = useRef<HTMLDivElement>(null);
  const [dropZoneElement, setDropZoneElement] = useState<HTMLDivElement | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openModal = useCallback((file?: File) => {
    setInitialFile(file);
    setIsModalOpen(true);
  }, []);

  const handleDroppedFile = useCallback(
    (file: File) => {
      openModal(file);
    },
    [openModal],
  );

  // Only the drop zone is a drop target — not the whole card
  useUppyDropTarget({
    element: dropZoneElement,
    enabled: !isModalOpen,
    onFile: handleDroppedFile,
    onDragStateChange: setIsDragging,
  });

  const handleBrowseClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && files[0]) {
      openModal(files[0]);
      e.target.value = '';
    }
  };

  const handleUploadFinished = (result: CalcSettingsUploadResult) => {
    const isSuccess = result.uploadResult?.status === 'Completed';
    setUploadComplete(isSuccess);
  };

  return (
    <Flex
      ref={cardRef}
      flexDirection="column"
      borderRadius="8px"
      backgroundColor="white"
      boxShadow="md"
      style={{
        border: uploadComplete
          ? `2px solid ${themeColors.interactive.positive.enabled}`
          : undefined,
      }}
    >
      {/* Header */}
      <Flex
        flexDirection="column"
        gap="8px"
        position="relative"
        padding={'24px 24px 12px 24px'}
      >
        {uploadComplete && (
          <Icon
            aria-label="upload successful"
            name="status-circle-check-solid"
            color="ds-color-core-green-600"
            size="medium"
            style={{ position: 'absolute', top: '0', right: '0' }}
          />
        )}
        <Text fontWeight="bold" fontSize="20px" lineHeight="1.3">
          {content.title}
        </Text>
        <Text fontSize="14px">
          Use the provided <DownloadButton target={target} inline /> to upload
          calculation settings.
        </Text>
        <Text fontSize="14px">
          Files must match the{' '}
          <RequiredFormatTooltip columns={content.description} />.
        </Text>
      </Flex>

      <div
        style={{
          borderTop: `1px dashed ${themeColors.support.neutral.muted}`,
          padding: '12px 24px 24px 24px',
        }}
      >
        {/* Drop zone — dropping a file opens the modal seeded; clicking browse opens the file picker */}
        <Flex
          ref={setDropZoneElement}
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap="12px"
          padding="24px 16px"
          style={{
            backgroundColor: isDragging
              ? themeColors.interactive.primary.subtle.hovered
              : themeColors.interactive.primary.subtle.selected,
            transition: 'background-color 0.15s ease',
          }}
        >
          <Text className="body-md-regular" fontWeight="500">
            Drag and drop file here or
          </Text>
          <Flex
            alignItems="center"
            gap="6px"
            onClick={handleBrowseClick}
            style={{ cursor: 'pointer' }}
          >
            <Icon
              name="folder"
              aria-label="browse"
              color="ds-color-interactive-primary-enabled"
              size="small"
            />
            <Text
              fontSize="14px"
              fontWeight="500"
              color={themeColors.interactive.primary.enabled}
            >
              browse files
            </Text>
          </Flex>
        </Flex>
      </div>

      <input
        type="file"
        accept=".xlsx"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileChange}
      />

      <CalcSettingsUploadModal
        isOpen={isModalOpen}
        triggerRef={cardRef}
        title={content.title}
        description={content.description}
        target={target}
        initialFile={initialFile}
        onClose={() => setIsModalOpen(false)}
        onUploadFinished={handleUploadFinished}
      />
    </Flex>
  );
};

export default FileUploadCard;
